import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import { useDisqualificationSave } from "@/queries/recruit/use-disqualifications.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiCandidateDisqualificationReason } from "@/types/recruit/disqualification-reason.types.ts";
import { Input, Modal, useModals, ModalProps, Button, Stack } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface CandidateDisqualificationReasonEditModelProps extends ModalProps {
  disqualificationReason: ApiCandidateDisqualificationReason;
}

export function CandidateDisqualificationReasonEditModal({
  disqualificationReason,
  ...props
}: CandidateDisqualificationReasonEditModelProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = { ...props };

  const CandidateDisqualificationReasonSchemaObject = z.object({
    title: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
  });

  type CandidateDisqualificationReasonSchema = z.infer<
    typeof CandidateDisqualificationReasonSchemaObject
  >;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CandidateDisqualificationReasonSchema>({
    mode: "onSubmit",
    resolver: zodResolver(CandidateDisqualificationReasonSchemaObject),
    defaultValues: {
      title: disqualificationReason.title,
    },
  });

  const disqualificationSave = useDisqualificationSave(control);

  const onSubmit = handleSubmit((data) => {
    disqualificationSave.mutate(
      {
        id: disqualificationReason.id,
        title: data.title,
      },
      {
        onSuccess: (data) => {
          if (disqualificationReason.id == 0) {
            analyticsService.trackEvent(analyticEvents.disqualificationReasons.created, {
              [analyticProperties.id]: data.id,
            });
          } else {
            analyticsService.trackEvent(analyticEvents.disqualificationReasons.edited, {
              [analyticProperties.id]: disqualificationReason.id,
            });
          }

          close(id);
        },
      }
    );
  });

  return (
    <Modal
      {...props}
      title={t("ats:disqualification_reason")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={disqualificationSave.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          label={t("common:title")}
          required={true}
          type={"text"}
          autoFocus={true}
          {...register("title")}
          error={errors.title?.message}
        />
      </form>
    </Modal>
  );
}
